import { defineStore } from 'pinia';
import posthog from 'posthog-js';
import { supabase } from '../supabase'
import ApiService from '../service/ApiService'
import { baseURL } from '../composables/environment'
import { reportActivationConversion } from '../composables/googleAds'

export const useUserStore = defineStore('user', {
    state: () => ({
        hasActiveSubscription: false,
        subscriptionBlocked: false,
        subscriptionBlockReason: null, // 'trial_expired' or 'subscription_expired'
        subscriptionStatusChecked: false,
        paywallHitTracked: false,
        trialInitialized: false,
        showSubscriptionModal: false,
        connectStatus: { status: 'not_started', charges_enabled: false, payouts_enabled: false },
        showConnectModal: false,
        accountId: null,
        hoaName: null,
    }),
    getters: {
        isConnectReady() {
            return this.connectStatus.status === 'complete';
        },
    },
    actions: {
        async fetchConnectStatus() {
            try {
                this.connectStatus = await new ApiService().getConnectStatus();
            } catch (error) {
                console.error('Error fetching connect status:', error);
            }
        },
        toggleConnectModal() {
            this.showConnectModal = !this.showConnectModal;
        },
        setSubscriptionStatus(status) {
            this.hasActiveSubscription = status;
        },
        toggleSubscriptionModal() {
            this.showSubscriptionModal = !this.showSubscriptionModal;
        },
        async initializeTrial() {
            if (this.trialInitialized) return;
            try {
                const { data: { session } } = await supabase.auth.getSession();
                if (!session) return;

                const response = await fetch(`${baseURL}/api/trial/start`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${session.access_token}`,
                    },
                });
                if (response.ok) {
                    this.trialInitialized = true;
                }
            } catch (error) {
                console.error('Error initializing trial:', error);
            }
        },
        async checkSubscriptionStatus() {
            try {
                const result = await this.fetchSubscriptionStatus();
                this.hasActiveSubscription = result.active;
                this.subscriptionStatusChecked = true;

                if (result.active) {
                    this.subscriptionBlocked = false;
                    this.subscriptionBlockReason = null;

                    // The pricing-table checkout completes on Stripe's domain and is
                    // recorded by a backend webhook, so the first status check that
                    // sees a paid subscription is the app's earliest signal that
                    // checkout finished. Repeat fires dedupe on the subscription id.
                    if (result.type === 'paid') {
                        const { data: { session } } = await supabase.auth.getSession();
                        reportActivationConversion(result.stripeSubscriptionId, session?.user?.email);
                    }
                } else {
                    this.subscriptionBlocked = true;
                    this.subscriptionBlockReason =
                        result.type === 'trial' ? 'trial_expired' : 'subscription_expired';

                    // Capture once per session when a user first hits the paywall,
                    // so we can target win-back outreach and measure recovery.
                    if (!this.paywallHitTracked) {
                        posthog.capture('trial_paywall_hit', {
                            block_reason: this.subscriptionBlockReason,
                        });
                        this.paywallHitTracked = true;
                    }
                }
            } catch (error) {
                console.error('Error checking subscription status:', error);
                // Fail-open: don't block users due to transient errors
                this.subscriptionBlocked = false;
            }
        },
        invalidateSubscriptionCache() {
            this.subscriptionStatusChecked = false;
        },
        async fetchSubscriptionStatus() {
            // Check if the user has a Stripe subscription

            const { data: { session } } = await supabase.auth.getSession();

            // Query to get user's subscription details through the relationship chain
            const { data, error } = await supabase
                .from('users')
                .select(`
            account:account_id (
              subscription:subscription_id (
                stripe_subscription_id, status, type
              )
            )
          `)
                .eq('user_id', session.user.id)
                .single()

            if (error) throw error

            const stripeSubId = data?.account?.subscription?.stripe_subscription_id
            const status = data?.account?.subscription?.status
            const type = data?.account?.subscription?.type

            const active = !!stripeSubId && (status === 'active' || status === 'trialing')
            return { active, type, status, stripeSubscriptionId: stripeSubId }
        },
        async getUserAccountId() {
          const { data: { user } } = await supabase.auth.getUser()
          if (!user) {
            throw new Error('No user is authenticated')
          }
      
          const { data, error } = await supabase
            .from('users')
            .select('account_id')
            .eq('user_id', user.id)
            .single()
      
          if (error) {
            throw error
          }
      
          if (!data.account_id) {
            throw new Error('No account_id found for user ', user.id)
          }
      
          this.accountId = data.account_id
        },
        async fetchHoaName() {
            const { data, error } = await supabase
                .from('accounts')
                .select('name')
                .eq('account_id', this.accountId)
                .single()

            if (error) throw error

            this.hoaName = data.name
        },
        async updateHoaName(name) {
            const { error } = await supabase
                .from('accounts')
                .update({ name })
                .eq('account_id', this.accountId)

            if (error) throw error

            this.hoaName = name

            // Sync to Stripe Connect account if one exists
            if (this.connectStatus.status !== 'not_started') {
                await new ApiService().updateConnectAccountName()
            }
        },
    },
});